import service, { cancelRequest } from '@/services';
import {
  PortfolioState,
  IncomeExpenseProjection,
  ProjectedSaving,
  HomePriceProjection,
  Portfolio,
} from '@/common/widgets/portfolio/util';
import { AssetMap, GetAccountsResponse } from '@/util/util';
import { getItemWithExpiry, type AccountType } from '@wavvest/shared-utils';
import { BankAccountData, WealthChartData, PlaidHoldingsData } from '@/util/plaid-util';
import { message } from 'antd';
import { RcFile } from 'antd/es/upload';
import { PlaidData } from './weave-x';
import { components } from '@/util/apex-client-gen';
import { unwrapDataArray, type MaybeDataEnvelope } from './response-envelope';
import type { InvestorLifecycleStatus } from '@/common/investor-lifecycle-status';

const SESSION_EXPIRED_MESSAGE = 'Your session has expired, please log in again.';
const INVESTOR_API_VERSION = '2025-06-16';

const expireStoredUserSession = (): never => {
  message.error(SESSION_EXPIRED_MESSAGE);
  window.location.href = '/';
  throw new Error(SESSION_EXPIRED_MESSAGE);
};

export const getUserData = () => {
  const userStr = getItemWithExpiry('user');
  if (!userStr) {
    expireStoredUserSession();
  }

  try {
    return JSON.parse(userStr);
  } catch (error) {
    console.error('Failed to parse stored user data:', error);
    expireStoredUserSession();
  }
};

export type IdentityVerificationUser = {
  date_of_birth: string;
  ip_address: string;
  email_address: string | null;
  name: {
    given_name: string;
    family_name: string;
  };
  address: {
    street: string;
    street2: string | null;
    city: string;
    region: string;
    postal_code: string;
    country: string;
  };
  id_number: {
    value: string;
    type: string;
  };
  phone_number: string;
};

export type IdentityVerificationTemplate = {
  id: string;
  version: number;
};

export type IdentityVerificationSteps = {
  accept_tos: string;
  verify_sms: string;
  kyc_check: string;
  documentary_verification: string;
  selfie_check: string;
  watchlist_screening: string;
  risk_check: string;
};

export type KycCheck = {
  status: string;
  address: {
    summary: string;
    po_box: string;
    type: string;
  };
  name: {
    summary: string;
  };
  date_of_birth: {
    summary: string;
  };
  id_number: {
    summary: string;
  };
  phone_number: {
    summary: string;
    area_code: string;
  };
};

export type RiskCheck = {
  status: string;
  behavior: {
    user_interactions: string;
    fraud_ring_detected: string;
    bot_detected: string;
  };
  email: any | null;
  phone: {
    linked_services: string[];
  };
  devices: Array<{
    ip_proxy_type: string;
    ip_spam_list_count: number;
    ip_timezone_offset: string;
  }>;
  identity_abuse_signals: {
    synthetic_identity: {
      score: number;
    };
    stolen_identity: {
      score: number;
    };
  };
};

export type VerifySms = {
  status: string;
  verifications: Array<{
    status: string;
    attempt: number;
    phone_number: string;
    delivery_attempt_count: number;
    solve_attempt_count: number;
    initially_sent_at: string;
    last_sent_at: string;
    redacted_at: string | null;
  }>;
};

export type IdentityVerificationData = {
  id: string;
  client_user_id: string;
  created_at: string;
  completed_at: string;
  previous_attempt_id: string | null;
  shareable_url: string;
  template: IdentityVerificationTemplate;
  user: IdentityVerificationUser;
  status: string;
  steps: IdentityVerificationSteps;
  documentary_verification: any | null;
  selfie_check: any | null;
  kyc_check: KycCheck;
  risk_check: RiskCheck;
  verify_sms: VerifySms;
  watchlist_screening_id: string;
  beacon_user_id: string | null;
  redacted_at: string | null;
  request_id: string;
};

export type IdentityVerificationResponse = {
  data: string; // JSON string, need to parse to IdentityVerificationData
};

export const getInvestorIdentityVerification = async (
  investorId: string,
): Promise<IdentityVerificationData> => {
  const url = `/customers/${investorId}/identity-verification`;
  try {
    const response = await service.getByParams<IdentityVerificationResponse>(url);
    // parse JSON string
    const parsedData: IdentityVerificationData = JSON.parse(response.data);
    return parsedData;
  } catch (error) {
    console.error('Failed to fetch investor identity verification data:', error);
    throw error;
  }
};

export const getCustomerIdentityVerification = getInvestorIdentityVerification;

export const getPlaidAggregatedData = async (investorId: string): Promise<PlaidData> => {
  try {
    const url = `/customers/${investorId}/plaid/aggregated`;
    const response = await service.getByParams<PlaidData>(url);
    return response;
  } catch (error) {
    console.error('Failed to get plaid aggregated data:', error);
    throw error;
  }
};

export const getApexData = async (
  investorId: string,
  apexCorrespondentId: string | undefined,
): Promise<GetAccountsResponse> => {
  try {
    if (apexCorrespondentId) {
      const url = `/correspondents/${apexCorrespondentId}/customers/${investorId}/apex-accounts`;
      const response = await service.getByParams<GetAccountsResponse>(url);
      return response;
    } else {
      throw new Error('Apex correspondent ID not found');
    }
  } catch (error) {
    console.error('Failed to get apex data:', error);
    throw error;
  }
};

export type GetAccountsV2Response = {
  accounts: {
    account_id: string;
    account_name: string;
    account_number: string;
    goal: AccountType;
    total_equity: string;
    total_equity_date: string;
  }[];
};

export const getApexAccountDataV2 = async (
  // correspondentId: string,
  investorId: string,
): Promise<GetAccountsV2Response> => {
  const url = `/customers/${investorId}/accounts-overview`;
  const response = await service.getByParams<GetAccountsV2Response>(url);
  return response;
};

export type GetAccountDetailResponse = {
  account_detail: components['schemas']['Account'];
};

export const getApexAccountDetailData = async (
  apexCorrespondentId: string,
  investorId: string,
  accountId: string,
): Promise<GetAccountDetailResponse> => {
  const url = `/correspondents/${apexCorrespondentId}/customers/${investorId}/accounts/${accountId}/detail`;
  try {
    const response = await service.getByParams<GetAccountDetailResponse>(url);
    return response;
  } catch (error) {
    console.error(`Failed to get account detail for ${accountId}:`, error);
    throw error;
  }
};

export type GetIraContributionConstraintsResponse = {
  max_contribution: string;
  current_year_contributions: string;
  available_contribution: string;
};

export type GetIraDistributionConstraintsResponse = {
  min_distribution: string;
  early_withdrawal_penalty: boolean;
};

export type GetAccountFinancialDetailResponse = {
  cash_available_for_withdraw: string;
  ira_contribution_constraints?: GetIraContributionConstraintsResponse;
  ira_distribution_constraints?: GetIraDistributionConstraintsResponse;
};

export const getApexAccountFinancialDetailData = async (
  investorId: string,
  accountId: string,
): Promise<GetAccountFinancialDetailResponse> => {
  const url = `/customers/${investorId}/accounts/${accountId}/financial-details`;
  try {
    const response = await service.getByParams<GetAccountFinancialDetailResponse>(url);
    return response;
  } catch (error) {
    console.error(`Failed to get account financial detail for ${accountId}:`, error);
    throw error;
  }
};

export type GetApexAccountAllocationResponse = {
  kind: string;
  risk_score: number;
  time_horizon_years: number;
};

export const getApexAccountAllocation = async (
  apexCorrespondentId: string,
  investorId: string,
  accountId: string,
): Promise<GetApexAccountAllocationResponse> => {
  const url = `/correspondents/${apexCorrespondentId}/customers/${investorId}/accounts/${accountId}/asset-allocation`;
  const response = await service.getByParams<GetApexAccountAllocationResponse>(url);
  return response;
};

export type GetHoldingsResponse = {
  holdings: components['schemas']['Position'][];
};

export const getApexHoldingsData = async (
  apexCorrespondentId: string,
  investorId: string,
  accountId: string,
): Promise<GetHoldingsResponse> => {
  const url = `/correspondents/${apexCorrespondentId}/customers/${investorId}/accounts/${accountId}/holdings`;
  const response = await service.getByParams<GetHoldingsResponse>(url);
  return response;
};

export type GetDepositsResponse = {
  deposits: components['schemas']['fo_apex_backend__routes__cash__GetDepositsResponse__Entry'][];
};
export type GetWithdrawalsResponse = {
  withdrawals: components['schemas']['fo_apex_backend__routes__cash__GetWithdrawalsResponse__Entry'][];
};

export const getApexDeposits = async (
  apexCorrespondentId: string,
  investorId: string,
  accountId: string,
): Promise<GetDepositsResponse> => {
  const queryParams = new URLSearchParams();
  queryParams.append('max_count', '16');
  queryParams.append('last_timestamp', new Date().toISOString());
  const url = `/correspondents/${apexCorrespondentId}/customers/${investorId}/accounts/${accountId}/deposits`;
  const fullUrl = queryParams.toString() ? `${url}?${queryParams.toString()}` : url;
  const response = await service.getByParams<GetDepositsResponse>(fullUrl);
  return response;
};

export const getApexWithdrawals = async (
  apexCorrespondentId: string,
  investorId: string,
  accountId: string,
): Promise<GetWithdrawalsResponse> => {
  const queryParams = new URLSearchParams();
  queryParams.append('max_count', '16');
  queryParams.append('last_timestamp', new Date().toISOString());
  const url = `/correspondents/${apexCorrespondentId}/customers/${investorId}/accounts/${accountId}/withdrawals`;
  const fullUrl = queryParams.toString() ? `${url}?${queryParams.toString()}` : url;
  const response = await service.getByParams<GetWithdrawalsResponse>(fullUrl);
  return response;
};

// One holding as firm-data records it, read through Weave's advisor route. The
// numeric fields are optional because isReadableFirmDataHolding accepts rows
// without them and the adapter treats a missing one as zero.
export type FirmDataHolding = {
  holding_id: string;
  account_id: string;
  instrument_id: string;
  quantity?: string;
  market_value?: string;
  cost_basis?: string | null;
  cost_basis_metadata?: unknown;
  as_of_date?: string | null;
  symbol?: string | null;
  name?: string | null;
  asset_class?: string | null;
};

// One transfer as firm-data records it. Direction lives on `transaction_type`
// and `amount` is a positive magnitude for both directions. `status` is null
// until the custodian reports one, which means unreported, not pending.
export type FirmDataTransaction = {
  transaction_id: string;
  account_id: string;
  transaction_type: string;
  amount: string;
  trade_date: string;
  settle_date?: string | null;
  external_transfer_id?: string | null;
  status?: string | null;
};

const isMissingFirmDataRead = (error: unknown): boolean =>
  error !== null &&
  typeof error === 'object' &&
  (error as { response?: { status?: unknown } }).response?.status === 404;

const isOptionalString = (value: unknown): boolean =>
  value === undefined || value === null || typeof value === 'string';

// The adapter reads these fields off every row, so a wrong type here would
// otherwise surface as a throw in the render, or as holdings quietly grouped
// under the wrong instrument.
const isReadableFirmDataHolding = (holding: unknown): boolean => {
  if (typeof holding !== 'object' || holding === null) return false;
  const row = holding as Record<string, unknown>;
  return (
    typeof row.instrument_id === 'string' &&
    typeof row.account_id === 'string' &&
    isOptionalString(row.as_of_date) &&
    (row.quantity === undefined || typeof row.quantity === 'string') &&
    (row.market_value === undefined || typeof row.market_value === 'string') &&
    isOptionalString(row.symbol) &&
    isOptionalString(row.name) &&
    isOptionalString(row.asset_class)
  );
};

// A row missing its amount would show as $0 and one missing its date as
// "Invalid Date", so every required field is checked rather than only the type
// the mapper reads.
const isReadableFirmDataTransaction = (transaction: unknown): boolean => {
  if (typeof transaction !== 'object' || transaction === null) return false;
  const row = transaction as Record<string, unknown>;
  return (
    typeof row.transaction_id === 'string' &&
    typeof row.account_id === 'string' &&
    typeof row.transaction_type === 'string' &&
    typeof row.amount === 'string' &&
    typeof row.trade_date === 'string'
  );
};

const unwrapFirmDataList = (response: unknown, key: string): unknown[] | null => {
  const body =
    response !== null && typeof response === 'object' && 'data' in response
      ? (response as { data?: unknown }).data
      : response;
  const list = (body as Record<string, unknown> | null | undefined)?.[key];
  return Array.isArray(list) ? list : null;
};

/**
 * Apex holdings for a firm-data investor, read from Weave's firm-data route
 * rather than per-account Apex calls. Omit `accountId` to read every account's
 * holdings in one call; pass firm-data's own account id (not the Apex
 * custodian id) to narrow it to one.
 *
 * A 404 is the route's "this firm is not on firm-data" answer, not a failure,
 * so it reads as nothing recorded.
 */
export const getFirmDataHoldings = async (
  investorId: string,
  accountId?: string,
): Promise<FirmDataHolding[]> => {
  const url = `/customers/${investorId}/wealth/holdings`;
  const fullUrl = accountId ? `${url}?${new URLSearchParams({ account_id: accountId })}` : url;
  try {
    const response = await service.getByParams<unknown>(
      fullUrl,
      {},
      { suppressErrorLog: isMissingFirmDataRead },
    );
    const holdings = unwrapFirmDataList(response, 'holdings');
    if (!holdings || !holdings.every(isReadableFirmDataHolding)) {
      throw new Error('invalid response data');
    }
    return holdings as FirmDataHolding[];
  } catch (error) {
    if (isMissingFirmDataRead(error)) return [];
    throw error;
  }
};

/**
 * Transfer history for a firm-data investor, read from Weave's firm-data
 * route. `accountId` is firm-data's own account id, not the Apex custodian id.
 * A 404 reads as nothing recorded, as for holdings.
 */
export const getFirmDataTransactions = async (
  investorId: string,
  accountId?: string,
): Promise<FirmDataTransaction[]> => {
  const url = `/customers/${investorId}/wealth/transactions`;
  const fullUrl = accountId ? `${url}?${new URLSearchParams({ account_id: accountId })}` : url;
  try {
    const response = await service.getByParams<unknown>(
      fullUrl,
      {},
      { suppressErrorLog: isMissingFirmDataRead },
    );
    const transactions = unwrapFirmDataList(response, 'transactions');
    if (!transactions || !transactions.every(isReadableFirmDataTransaction)) {
      throw new Error('invalid response data');
    }
    return transactions as FirmDataTransaction[];
  } catch (error) {
    if (isMissingFirmDataRead(error)) return [];
    throw error;
  }
};

export type TimeRange = '1W' | '1M' | '3M' | '1Y' | 'YTD' | 'ALL';

export const getApexChartData = async (
  apexCorrespondentId: string,
  investorId: string,
  accountIds?: string[],
  range?: TimeRange,
): Promise<WealthChartData> => {
  const url = `/correspondents/${apexCorrespondentId}/customers/${investorId}/charts`;
  const queryParams = new URLSearchParams();
  accountIds?.forEach((id) => queryParams.append('account_ids', id));
  range && queryParams.append('range', range);

  const fullUrl = queryParams.toString() ? `${url}?${queryParams}` : url;
  const response = await service.getByParams<WealthChartData>(fullUrl);
  return response;
};

export type BankLinkCurrentLink = {
  plaid_id?: string | null;
  relationship_id: string;
  state: string;
  masked_number?: string;
};

export type BankLinkWallet = {
  account_id: string;
  current_link?: BankLinkCurrentLink | null;
};

export type BankLinkOptions = {
  wallets: BankLinkWallet[];
};

export const getApexBankLinkOptions = async (
  apexCorrespondentId: string,
  investorId: string,
): Promise<BankLinkOptions> =>
  service.getByParams<BankLinkOptions>(
    `/correspondents/${apexCorrespondentId}/customers/${investorId}/bank-link-options`,
  );

export type getBankAccountsResponse = {
  accounts: BankAccountData[];
  summary: {
    total_assets: number;
    total_investments: number;
    total_liabilities: number;
    net_worth: number;
  };
} | null;

export type getPlaidWealthSnapshotResponse = {
  snapshot_id: string;
  as_of: string;
  summary: {
    total_assets: number;
    total_investments?: number;
    total_liabilities: number;
    net_worth: number;
  };
  accounts: BankAccountData[];
  chart: WealthChartData;
  chart_latest: {
    date: string | null;
    value: number | null;
  };
  last_plaid_value_dates?: Record<string, string | null>;
} | null;

export const getPlaidBankAccounts = async (
  investorId: string,
): Promise<getBankAccountsResponse> => {
  const url = `/customers/${investorId}/plaid/accounts`;
  try {
    const response = await service.getByParams<
      getBankAccountsResponse | { status: string; message: string; data: getBankAccountsResponse }
    >(url);
    if (!response) return null;
    // Weave backend wraps payload in { status, message, data }; use data when present
    if ('data' in response) {
      if (
        response.data != null &&
        typeof response.data === 'object' &&
        'accounts' in response.data
      ) {
        return response.data as getBankAccountsResponse;
      }
      // data exists but is null or not a valid payload
      return null;
    }
    return response as getBankAccountsResponse;
  } catch (error) {
    console.error('Failed to get bank accounts:', error);
    return null;
  }
};

const errorResponseDetail = (error: unknown): string | undefined => {
  if (error === null || typeof error !== 'object' || !('response' in error)) {
    return undefined;
  }

  const response = (error as { response?: { data?: unknown } }).response;
  const data = response?.data;
  if (typeof data === 'string') return data;
  if (data === null || typeof data !== 'object') return undefined;

  const detail = (data as { detail?: unknown }).detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && 'message' in detail) {
    const message = (detail as { message?: unknown }).message;
    return typeof message === 'string' ? message : undefined;
  }

  const message = (data as { message?: unknown }).message;
  return typeof message === 'string' ? message : undefined;
};

const EXPECTED_PLAID_WEALTH_SNAPSHOT_DETAILS = new Set([
  'Customer identity not found',
  'MISSING_IDENTITY_MAPPING',
  'INVESTOR_NOT_IN_FIRM',
  'ADVISOR_NOT_AUTHORIZED',
]);

const isExpectedMissingPlaidWealthSnapshot = (error: unknown): boolean => {
  if (error === null || typeof error !== 'object' || !('response' in error)) {
    return false;
  }

  const response = (error as { response?: { status?: unknown } }).response;
  if (response?.status !== 403 && response?.status !== 404) return false;

  const detail = errorResponseDetail(error);
  return typeof detail === 'string' && EXPECTED_PLAID_WEALTH_SNAPSHOT_DETAILS.has(detail);
};

export const getPlaidWealthSnapshot = async (
  investorId: string,
  accountIds?: string[],
  range?: TimeRange,
): Promise<getPlaidWealthSnapshotResponse> => {
  const url = `/customers/${investorId}/plaid/snapshot`;
  const queryParams = new URLSearchParams();
  accountIds?.forEach((id) => queryParams.append('account_ids', id));
  range && queryParams.append('range', range);

  const fullUrl = queryParams.toString() ? `${url}?${queryParams}` : url;
  const isPlaidWealthSnapshotPayload = (
    payload: unknown,
  ): payload is getPlaidWealthSnapshotResponse => {
    return (
      payload != null &&
      typeof payload === 'object' &&
      'summary' in payload &&
      'accounts' in payload &&
      'chart' in payload
    );
  };

  try {
    const response = await service.getByParams<
      | getPlaidWealthSnapshotResponse
      | {
          status: string;
          message: string;
          data: getPlaidWealthSnapshotResponse;
        }
    >(fullUrl, {}, { suppressErrorLog: isExpectedMissingPlaidWealthSnapshot });
    if (!response) return null;
    if ('data' in response) {
      if (isPlaidWealthSnapshotPayload(response.data)) {
        return response.data as getPlaidWealthSnapshotResponse;
      }
      return null;
    }

    if (isPlaidWealthSnapshotPayload(response)) {
      return response as getPlaidWealthSnapshotResponse;
    }

    return null;
  } catch (error) {
    if (isExpectedMissingPlaidWealthSnapshot(error)) return null;
    console.error('Failed to get plaid wealth snapshot:', error);
    throw error;
  }
};

export const getPlaidChartData = async (
  investorId: string,
  accountIds?: string[],
  range?: TimeRange,
): Promise<WealthChartData> => {
  const url = `/customers/${investorId}/plaid/chart`;
  const queryParams = new URLSearchParams();
  accountIds?.forEach((id) => queryParams.append('account_ids', id));
  range && queryParams.append('range', range);

  const fullUrl = queryParams.toString() ? `${url}?${queryParams}` : url;
  const response = await service.getByParams<WealthChartData>(fullUrl);
  return response;
};

export const getPlaidHoldings = async (investorId: string): Promise<PlaidHoldingsData> => {
  const url = `/customers/${investorId}/plaid/holdings`;
  const response = await service.getByParams<PlaidHoldingsData>(url);
  return response;
};

export const getApexAssetMapData = async (
  apexCorrespondentId: string | undefined,
): Promise<AssetMap> => {
  try {
    if (apexCorrespondentId) {
      const url = `/correspondents/${apexCorrespondentId}/asset-map`;
      const response = await service.getByParams<AssetMap>(url);
      return response;
    } else {
      const profileData = await getProfile();
      const apexCorrespondentId = profileData.profile.apex_correspondent_id;
      if (!apexCorrespondentId) {
        throw new Error('Apex correspondent ID not found');
      }
      const url = `/correspondents/${apexCorrespondentId}/asset-map`;
      const response = await service.getByParams<AssetMap>(url);
      return response;
    }
  } catch (error) {
    console.error('Failed to get apex asset map data:', error);
    throw error;
  }
};

export type PlanData = {
  id: string;
  sharedBy: string;
  sharedAt: number;
  visualizationHtml: {
    fileName: string;
    presignedUrl: string;
    sourceCode: string;
  };
  title: string;
  userAnswers: Record<string, any>;
};

export const getVisualizationData = async (investorId: string): Promise<PlanData[]> => {
  const url = `/customers/${investorId}/shared/visualization`;
  try {
    const response = await service.getByParams<PlanData[]>(url);
    return response;
  } catch (error) {
    console.error('Failed to get visualization data:', error);
    throw error;
  }
};

export const generateInvestorSignupLink = async (investorId: string): Promise<string> => {
  const user = getUserData();
  const url = `/firms/${user.client_id}/investors/${investorId}/onboarding-link`;
  const response = await service.post<string | InvestorSignupLinkResponse>(url, {});
  return typeof response === 'string' ? response : response.data;
};

export type InvestorSignupLinkResponse = {
  status: string;
  message: string;
  data: string;
};

export const getPermInviteToken = async (): Promise<string> => {
  const user = getUserData();
  const url = `/clients/${user.client_id}/perm-invite-link`;
  const response = await service.getByParams<string>(url);
  const token = new URL(response, window.location.origin).searchParams.get('token');
  if (!token) {
    throw new Error('Perm invite link is missing token');
  }
  return token;
};

export type answersItem = {
  id: string;
  content: string;
  growth: string;
  capital_preservation: string;
};

export type questionsContent = {
  context: string;
  question: string;
  answers: answersItem[];
};

export type RiskassessementData = {
  questions: questionsContent[];
};

export const getRiskassessement = async (): Promise<RiskassessementData> => {
  const user = getUserData(),
    url = `/clients/${user.client_id}/riskassessment`;
  try {
    const response = await service.getByParams<RiskassessementData>(url);
    return response;
  } catch (error) {
    console.error('Failed to fetch Riskassessement data:', error);
    throw error;
  }
};

export const replaceRiskassessement = async (
  data: RiskassessementData,
): Promise<RiskassessementData> => {
  const user = getUserData(),
    url = `/clients/${user.client_id}/riskassessment`;
  try {
    const response = await service.put<RiskassessementData>(url, data);
    return response;
  } catch (error) {
    console.error('Failed to replace Riskassessement data:', error);
    throw error;
  }
};

export type FilehubFilesData = {
  key: string;
  s3_key: string;
  filename: string;
  size_byte: number;
  upload_at: string;
  mime_type: string;
  owner_cognito_id: string;
  owner_type: 'advisor' | 'enduser';
  is_public: boolean;
  requires_document_processing: boolean;
  document_processing_state: boolean;
  shares: string[];
  client_id?: string;
};

export type FilehubFilesResponse = {
  files: FilehubFilesData[];
  pagination_key: string;
};

export type PaginationFilesResponse = {
  files: FilehubFilesData[];
  pagination_key: string;
  total_count: number;
  current_page: number;
  total_pages: number;
  has_more: boolean;
};

export const getFilehubFiles = async (): Promise<FilehubFilesResponse> => {
  const user = getUserData(),
    url = `/clients/${user.client_id}/filehub`;
  try {
    const response = await service.getByParams<FilehubFilesResponse>(url);
    return response;
  } catch (error) {
    console.error('Failed to fetch FilehubFiles data:', error);
    throw error;
  }
};

export type uploadFilehubFileResponse = {
  file: FilehubFilesData;
};

export const uploadFilehubFile = async (formData: FormData): Promise<uploadFilehubFileResponse> => {
  const user = getUserData(),
    presign_url = `/clients/${user.client_id}/filehub/presigned-upload`;
  try {
    const response = await service.post<uploadFilehubFileResponse>(presign_url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  } catch (error) {
    console.error('Failed to upload Filehub file data:', error);
    throw error;
  }
};

export type downloadFilehubFileResponse = {
  content: string;
  filename: string;
  mime_type: string;
};

export const downloadFilehubFile = async (fileId: string): Promise<downloadFilehubFileResponse> => {
  const user = getUserData(),
    url = `/clients/${user.client_id}/shared_files/${fileId}`;
  try {
    const response = await service.getByData<downloadFilehubFileResponse>(url);
    return response;
  } catch (error) {
    console.error('Failed to download Filehub file data:', error);
    throw error;
  }
};

export type DeleteFilehubFileResponse = {
  status: string;
  message: string;
  data: uploadFilehubFileResponse;
};

export const deleteFilehubFile = async (fileId: string): Promise<DeleteFilehubFileResponse> => {
  const user = getUserData(),
    url = `/clients/${user.client_id}/filehub/${fileId}`;
  try {
    const response = await service.delByData<DeleteFilehubFileResponse>(url);
    return response;
  } catch (error) {
    console.error('Failed to delete Filehub file:', error);
    throw error;
  }
};

export type MembersData = {
  id: string;
  client_id: string;
  name: string;
  email: string;
  role: string;
};

export type MemberRole = 'member' | 'admin';
export type MemberAccountStatus = 'ACTIVE' | 'DISABLED';

export type MembersResponse = {
  members: MembersData[];
};

export type ChangeMemberRoleResponse = {
  status: string;
  data: MembersData;
};

export const getMembers = async (): Promise<MembersResponse> => {
  const user = getUserData(),
    url = `/clients/${user.client_id}/members`;
  try {
    const response = await service.getByParams<MembersResponse>(url);
    return response;
  } catch (error) {
    console.error('Failed to fetch Members data:', error);
    throw error;
  }
};

export const updateMemberRole = async (
  memberId: string,
  newRole: MemberRole,
): Promise<ChangeMemberRoleResponse> => {
  const url = `/members/${memberId}/change-role`;
  try {
    return await service.post<ChangeMemberRoleResponse>(url, { new_role: newRole });
  } catch (error) {
    console.error('Failed to update member role:', error);
    throw error;
  }
};

export const updateMemberAccountStatus = async (
  memberId: string,
  status: MemberAccountStatus,
): Promise<void> => {
  const action = status === 'ACTIVE' ? 'enable' : 'disable';
  const url = `/members/${memberId}/${action}`;
  try {
    await service.post<void>(url);
  } catch (error) {
    console.error('Failed to update member account status:', error);
    throw error;
  }
};

export type ProfileData = {
  id: string;
  brand_id?: string;
  name: string;
  introduction: string;
  logo_uri: string;
  logo_light_uri?: string;
  text_logo_uri: string;
  light_text_logo_uri?: string;
  video_uri: string;
  logo_mark_uri: string;
  compact_logo_uri?: string;
  compact_logo_light_uri?: string;
  customize_color: string;
  background_uri: string;
  landing_color: string;
  secondary_color?: string;
  name_color: string;
  co_branding: boolean;
  apex_correspondent_id: string;
  alt_investment_enabled: boolean;
  /**
   * Optional unique, human-readable slug used in branded client portal URLs
   * (e.g. /firm/clayvvest, /firm/clayvvest/signup). Empty string means no
   * alias is configured; clients still load via the `brand_id` lookup.
   * Allowed: 3-32 chars, lowercase letters, digits, and hyphens
   * (no leading/trailing/double hyphens, no reserved words).
   */
  alias?: string;
  /**
   * Default color theme applied to first-time users who have not selected a
   * personal theme preference. The backend always expects a resolved value of
   * either `light` or `dark` (a "system default" choice is resolved against the
   * viewer's OS preference before being persisted).
   */
  default_theme?: 'light' | 'dark';
};

export type ClientFeaturesRecord = {
  client_id: string;
  tier_id: string;
  april_enabled: boolean; // flag
  ladder_enabled: boolean; // flag
  estate_planning_enabled: boolean; // flag
  apex_enabled: boolean; // flag
  canvas_enabled: boolean; // flag
  firm_data_enabled?: boolean; // flag
  advisor_prep_dashboard_enabled?: boolean; // flag
  investor_record_autogenerated_summary_enabled?: boolean; // flag
  investor_record_todo_enabled?: boolean; // flag
};

export type ClientTierChoiceRecord = {
  client_id: string;
  current_tier_id: string;
  next_tier_id: string | null;
};

export type StripeInfoRecord = {
  subscription_status?: string | null;
  no_subscription?: boolean;
  snapshot_at?: string | null;
  client_id: string;
  customer_id: string;
  subscription_id: string;
  active: boolean;
  product_id: string;
};

export type GetProfileResponse = {
  profile: ProfileData;
  features: ClientFeaturesRecord | null;
  tier_choice: ClientTierChoiceRecord | null;
  stripe_info: StripeInfoRecord | null;
};

export const getProfile = async (): Promise<GetProfileResponse> => {
  const user = getUserData(),
    url = `/clients/${user.client_id}/profile`;
  try {
    const response = await service.getByParams<GetProfileResponse>(url);
    return response;
  } catch (error) {
    console.error('Failed to fetch Profile data:', error);
    throw error;
  }
};

export const updateProfile = async (data: ProfileData): Promise<GetProfileResponse> => {
  const user = getUserData(),
    url = `/clients/${user.client_id}/profile`;
  try {
    const response = await service.patch<GetProfileResponse>(url, data);
    return response;
  } catch (error) {
    console.error('Failed to update Profile data:', error);
    throw error;
  }
};

export const getInvestorFinancialPlans = async (investorId: string): Promise<PortfolioState[]> => {
  const url = `/customers/${investorId}/financial-plans`;
  try {
    const response = await service.getByParams<PortfolioState[]>(url);
    return response;
  } catch (error) {
    console.error('Failed to fetch investor financial plans data:', error);
    throw error;
  }
};

export const editInvestorFinancialPlans = async <T>(
  investorId: string,
  data: PortfolioState,
): Promise<T> => {
  const url = `/customers/${investorId}/financial-plans`;
  try {
    const response = await service.post<T>(url, data);
    return response;
  } catch (error) {
    console.error('Failed to edit investor financial plans data:', error);
    throw error;
  }
};

export const getCustomerFinancialPlans = getInvestorFinancialPlans;
export const editCustomerFinancialPlans = editInvestorFinancialPlans;

export type IncomeExpenseProjectionProps = {
  initialIncome: number;
  initialExpenses: number;
  currentAge: number;
  retirementAge: number;
  longevity: number;
  initialSavings: number;
  incomeGrowthRate?: number;
  expensesGrowthRate?: number;
  currentYear?: number;
};

export const generateIncomeExpenseProjection = async (
  investorId: string,
  data: IncomeExpenseProjectionProps,
): Promise<IncomeExpenseProjection[]> => {
  const url = `/customers/${investorId}/income-expense-projection`;
  try {
    const response = await service.post<IncomeExpenseProjection[]>(url, data);
    return response;
  } catch (error) {
    console.error('Failed to generate IncomeExpenseProjection data:', error);
    throw error;
  }
};

export type ProjectedSavingProps = {
  initial_income: number;
  initial_expenses: number;
  current_age: number;
  retirement_age: number;
  longevity: number;
  initial_savings: number;
  current_year?: number;
  portfolios: Portfolio[];
};

export const generateProjectedSaving = async (
  investorId: string,
  data: ProjectedSavingProps,
): Promise<ProjectedSaving[]> => {
  const url = `/customers/${investorId}/projected-savings`;
  try {
    const response = await service.post<ProjectedSaving[]>(url, data);
    return response;
  } catch (error) {
    console.error('Failed to generate ProjectedSaving data:', error);
    throw error;
  }
};

export type HomePriceProjectionProps = {
  currentValue: number;
  currentAge: number;
  longevity: number;
};

export const generateHomePriceProjection = async (
  investorId: string,
  data: HomePriceProjectionProps,
): Promise<HomePriceProjection[]> => {
  const url = `/customers/${investorId}/home-price-projection`;
  try {
    const response = await service.post<HomePriceProjection[]>(url, data);
    return response;
  } catch (error) {
    console.error('Failed to generate HomePriceProjection data:', error);
    throw error;
  }
};

export type ProbabilityOfSuccessProps = {
  annualIncome: number;
  annualExpense: number;
  currentSavings: number;
  currentAge: number;
  retirementAge: number;
  longevity: number;
  annualWithdrawal: number;
  portfolios: Portfolio[];
};

export type ProbabilityOfSuccessRes = {
  probability_of_success: number;
};

export const CalculateProbabilityOfSuccess = async (
  investorId: string,
  data: ProbabilityOfSuccessProps,
): Promise<ProbabilityOfSuccessRes> => {
  try {
    const url = `/customers/${investorId}/probability-of-success`;
    const response = await service.post<ProbabilityOfSuccessRes>(url, data);
    return response;
  } catch (error) {
    console.error('Failed to calculate ProbabilityOfSuccess data:', error);
    throw error;
  }
};

export type ConnectedGoogleAccount = {
  email: string;
  calendars: GoogleCalendar[];
};

export type GetGoogleAccountResponse = {
  google_accounts: ConnectedGoogleAccount[];
};

export const getConnectedGoogleAccounts = async (): Promise<GetGoogleAccountResponse> => {
  const user = getUserData(),
    url = `/members/${user.id}/google-accounts`;
  try {
    const response = await service.getByParams<GetGoogleAccountResponse>(url);
    return response;
  } catch (error) {
    console.error('Failed to fetch calendar data:', error);
    throw error;
  }
};

export type ConnectNewGoogleAccountResponse = {
  authUrl: string;
};

export const connectNewGoogleAccount = async (): Promise<ConnectNewGoogleAccountResponse> => {
  const user = getUserData(),
    url = `/members/${user.id}/google-accounts/connect-new`;
  try {
    const response = await service.post<ConnectNewGoogleAccountResponse>(url);
    return response;
  } catch (error) {
    console.error('Failed to connect new Google account:', error);
    throw error;
  }
};

export const disconnectGoogleAccount = async (email: string) => {
  const user = getUserData(),
    url = `/members/${user.id}/google-accounts/${email}`;
  try {
    const response = await service.delParams(url);
    return response;
  } catch (error) {
    console.error('Failed to disconnect Google account:', error);
    throw error;
  }
};

export const submitGoogleAccountAuthCode = async (code: string) => {
  const user = getUserData(),
    url = `/members/${user.id}/google-accounts/submit-auth-code?code=${code}`;
  try {
    await service.post<{ authUrl: string }>(url);
  } catch (error) {
    console.error('Failed to submit Google account auth code:', error);
    throw error;
  }
};

export interface GoogleCalendar {
  id: string;
  title: string;
  subscribed: boolean;
}

export type GoogleCalendarNotetakerStatus =
  | 'scheduled'
  | 'join_scheduled'
  | 'starting'
  | 'signing_in'
  | 'joining'
  | 'waiting_for_admission'
  | 'waiting_for_host'
  | 'recording'
  | 'processing'
  | 'completed'
  | 'notes_ready'
  | 'needs_reconnect'
  | 'blocked_by_meeting_policy'
  | 'blocked_by_google_risk'
  | 'captcha_or_account_challenge'
  | 'host_denied'
  | 'failed'
  | 'disabled';

export type GoogleCalendarNotetakerInvitationState =
  | 'not_configured'
  | 'not_required'
  | 'pending'
  | 'invited'
  | 'failed'
  | 'missing_calendar_scope'
  | 'event_not_editable'
  | 'event_not_found';

export interface GoogleCalendarEvent {
  schedule_id: string;
  job_id?: string;
  event_id: string;
  recurring_event_id?: string;
  calendar_id?: string;
  title: string;
  meeting_type: 'GoogleMeet';
  meeting_link: string;
  start_time: string;
  end_time: string;
  status: 'Scheduled' | 'UserDisabled';
  notetaker_status?: GoogleCalendarNotetakerStatus;
  notetaker_invitation_state?: GoogleCalendarNotetakerInvitationState;
  notetaker_bot_account_email?: string;
  oauth_id: string;
  oauth_source: 'Google';
  attendee_emails?: Array<string>;
  attendees?: Array<MeetingAttendee>;
  invited_attendee_emails?: Array<string>;
  invited_attendees?: Array<MeetingAttendee>;
}

export type GoogleCalendarEventResponse = {
  scheduled_meetings: GoogleCalendarEvent[];
};

export const getGoogleCalendarEvents = async (): Promise<GoogleCalendarEventResponse> => {
  const user = getUserData(),
    url = `/members/${user.id}/scheduled-meetings`;
  try {
    const response = await service.getByParams<GoogleCalendarEventResponse>(url);
    return response;
  } catch (error) {
    console.error('Failed to fetch Google calendar Events data:', error);
    throw error;
  }
};

export type SyncGoogleCalendarEventsResponse = {
  synced_accounts: number;
  failed_accounts: number;
};

export async function syncGoogleCalendarEvents(): Promise<SyncGoogleCalendarEventsResponse> {
  const user = getUserData(),
    url = `/members/${user.id}/scheduled-meetings/sync`;
  try {
    const response = await service.post<SyncGoogleCalendarEventsResponse>(url);
    return response;
  } catch (error) {
    console.error('Failed to synchronize Google calendar Events data:', error);
    throw error;
  }
}

export async function updateGoogleCalendars(account_email: string, data: object) {
  const user = getUserData(),
    url = `/members/${user.id}/google-accounts/${account_email}/calendars`;
  try {
    const response = await service.patch(url, data);
    return response;
  } catch (error) {
    console.error('Failed to patch calendars:', error);
    throw error;
  }
}

export type ScheduledMeetingRecurrenceScope = 'this_event' | 'this_and_following_events';

export type UpdateFutureMeetingsRequest = {
  patches: Array<{
    op: 'replace';
    schedule_id: string;
    field: 'status';
    value: GoogleCalendarEvent['status'] | null;
    scope?: ScheduledMeetingRecurrenceScope;
    firm_data_investor_id?: string | null;
  }>;
};

export async function updateFutureMeetings(data: UpdateFutureMeetingsRequest) {
  const user = getUserData(),
    url = `/members/${user.id}/scheduled-meetings`;
  try {
    const response = await service.patch(url, data);
    return response;
  } catch (error) {
    console.error('Failed to update future meetings:', error);
    throw error;
  }
}

export type StartScheduledMeetingNowResponse = {
  job_id: string;
  scheduled_meeting: GoogleCalendarEvent;
};

export async function startScheduledMeetingNow(
  scheduleId: string,
): Promise<StartScheduledMeetingNowResponse> {
  const user = getUserData(),
    url = `/members/${user.id}/scheduled-meetings/${scheduleId}/start-now`;
  try {
    const response = await service.post<StartScheduledMeetingNowResponse>(url);
    return response;
  } catch (error) {
    console.error('Failed to start scheduled meeting:', error);
    throw error;
  }
}

export type AttendedMeeting = {
  job_id: string;
  title: string;
  url: string;
  status: string;
  meeting_type: 'GoogleMeet';
  recording_link: string | null;
  transcript_file_key: string | null;
  summary_file_key: string | null;
  start_time: string;
  end_time?: string | null;
  attendee_emails?: Array<string>;
  attendees?: Array<MeetingAttendee>;
  invited_attendee_emails?: Array<string>;
  invited_attendees?: Array<MeetingAttendee>;
  attended_attendee_emails?: Array<string>;
  attended_attendees?: Array<MeetingAttendee>;
  meeting_memory_status?:
    | 'bound'
    | 'client_selection_needed'
    | 'ambiguous_client_match'
    | 'client_not_authorized'
    | 'binding_failed'
    | 'not_applicable';
  meeting_memory_skip_reason?: string;
};

export const isAttendedMeeting = (
  event: GoogleCalendarEvent | AttendedMeeting,
): event is AttendedMeeting => !('schedule_id' in event);

export type MeetingAttendee = {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  phone_number?: string | null;
};

export type GetAttendedMeetingsResponse = {
  meetings: AttendedMeeting[];
  pagination_key_base64?: string | null;
};

export type GetAttendedMeetingsParams = {
  paginationKey?: string | null;
  limit?: number;
};

export async function getAttendedMeetings(
  params: GetAttendedMeetingsParams = {},
): Promise<GetAttendedMeetingsResponse> {
  const user = getUserData();
  const url = `/members/${user.id}/attended-meetings`;
  const queryParams = {
    ...(params.paginationKey ? { pagination_key: params.paginationKey } : {}),
    ...(typeof params.limit === 'number' ? { limit: params.limit } : {}),
  };

  try {
    const response = await service.getByParams<GetAttendedMeetingsResponse>(
      url,
      Object.keys(queryParams).length > 0 ? queryParams : undefined,
    );
    return response;
  } catch (error) {
    console.error('Failed to fetch attended meetings data:', error);
    throw error;
  }
}

export async function getAttendedMeeting(jobId: string): Promise<AttendedMeeting | null> {
  const user = getUserData();
  const url = `/members/${user.id}/attended-meetings/lookup`;

  try {
    const response = await service.getByParams<AttendedMeeting | null>(url, { job_id: jobId });
    return response;
  } catch (error) {
    console.error('Failed to fetch attended meeting data:', error);
    throw error;
  }
}

export type GenerateRecordingLinkResponse = {
  recording: string;
};

export async function generateRecordingLink(jobId: string): Promise<GenerateRecordingLinkResponse> {
  const user = getUserData();
  const url = `/members/${user.id}/attended-meetings/${jobId}/recording`;
  try {
    const response = await service.getByParams<GenerateRecordingLinkResponse>(url);
    return response;
  } catch (error) {
    console.error('Failed to generate recording Link:', error);
    throw error;
  }
}

export type GetDocumentResponse = {
  document: {
    filename: string;
    file_size: number;
    content: string;
  };
};

export async function getDocument(fileKey: string) {
  const user = getUserData(),
    url = `/clients/${user.client_id}/documents/${fileKey}`;
  try {
    const response = await service.getByParams<GetDocumentResponse>(url);
    return response;
  } catch (error) {
    console.error('Failed to fetch document data:', error);
    throw error;
  }
}

export const cancelDocumentRequest = (fileKey: string) => {
  const user = getUserData(),
    url = `/clients/${user.client_id}/documents/${fileKey}`;
  cancelRequest(url);
};

export async function updateDocumentContent(key: string, content: string) {
  const user = getUserData(),
    url = `/clients/${user.client_id}/documents/${key}/content`;
  try {
    const response = await service.put(url, content);
    return response;
  } catch (error) {
    console.error('Failed to cancel Document request:', error);
    throw error;
  }
}

export async function startImmediateMeeting(data: {
  title?: string;
  url: string;
  meeting_length_minutes?: number;
  meeting_type?: 'GoogleMeet';
}) {
  const user = getUserData(),
    url = `/members/${user.id}/start-immediate-meeting`;
  try {
    const response = await service.post(url, data);
    return response;
  } catch (error) {
    console.error('Failed to start immediate meeting:', error);
    throw error;
  }
}

export type uploadProfileMediaResponse = {
  media_type: string;
  url: string;
};

export const uploadProfileMedia = async (
  formData: FormData,
  media_type: 'logo' | 'video' | 'logo_mark' | 'text_logo' | 'light_text_logo',
): Promise<uploadProfileMediaResponse> => {
  const user = getUserData();
  const url = `/clients/${user.client_id}/profile/media/${media_type}`;
  try {
    const response = await service.post<uploadProfileMediaResponse>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 0,
    });
    return response;
  } catch (error) {
    console.error('Failed to upload profile media:', error);
    throw error;
  }
};

export type uploadProfileMediaByPresignedURLResponse = {
  media_type: string;
  url: string;
  presigned_url: string;
  form_fields: {
    [key: string]: string;
  };
};

export const getPresignedURL = async (data: {
  file_name: string;
  media_type:
    | 'logo'
    | 'logo_light'
    | 'video'
    | 'logo_mark'
    | 'compact_logo'
    | 'compact_logo_light'
    | 'background'
    | 'text_logo'
    | 'light_text_logo';
  file_type: string;
}): Promise<uploadProfileMediaByPresignedURLResponse> => {
  const user = getUserData();
  const presignedUrl = `/clients/${user.client_id}/profile/media/presigned-url`;

  try {
    const presignedUrlResponse =
      await service.getByParams<uploadProfileMediaByPresignedURLResponse>(presignedUrl, data);
    return presignedUrlResponse;
  } catch (error) {
    console.error('Failed to upload profile media:', error);
    throw error;
  }
};

export type TierFeaturesRecord = {
  tier_id: string;
  title: string;
  description: string;
  deprecated: boolean;
  april_enabled: boolean; // flag
  ladder_enabled: boolean; // flag
  estate_planning_enabled: boolean; // flag
  apex_enabled: boolean; // flag
};

export type ListTiersResponse = {
  tiers: TierFeaturesRecord[];
};

export const listTiers = async (): Promise<ListTiersResponse> => {
  try {
    const url = `/tiers`;
    const response = await service.getByParams<ListTiersResponse>(url);
    return response;
  } catch (error) {
    console.error('Failed to get tier list', error);
    throw error;
  }
};

export const updateTier = async (tierId: string): Promise<GetProfileResponse> => {
  const user = getUserData(),
    url = `/clients/${user.client_id}/update-tier`;
  try {
    const response = await service.post<GetProfileResponse>(url, {
      tier_id: tierId,
    });
    return response;
  } catch (error) {
    console.error('Failed to update tier choice', error);
    throw error;
  }
};

export type CorrespondentStats = {
  total_fee: string;
  aum: string;
};

export const getCorrespondentStats = async (): Promise<CorrespondentStats> => {
  const user = getUserData();
  const url = `/clients/${user.client_id}/correspondent_stats`;
  try {
    const response = await service.getByParams<CorrespondentStats>(url);
    return response;
  } catch (error) {
    console.error('Failed to fetch correspondent stats:', error);
    throw error;
  }
};

export const getClientPortalUrl = async (customerId: string): Promise<string> => {
  const url = `/client-portal`;
  try {
    const response = await service.getByParams<string>(url, {
      customer_id: customerId,
    });
    return response;
  } catch (error) {
    console.error('Failed to get client portal url', error);
    throw error;
  }
};

export const getCustomerSecret = async (customerId: string): Promise<string> => {
  const url = `/customer/secret`;
  try {
    const response = await service.getByParams<string>(url, {
      customer_id: customerId,
    });
    return response;
  } catch (error) {
    console.error('Failed to get customer secret', error);
    throw error;
  }
};

export type getFilehubPresignedURLResponse = {
  presigned_body: {
    url: string;
    fields: {
      [key: string]: string;
    };
  };
  data: FilehubFilesData;
};

export const getFilehubPresignedURL = async (
  params: {
    is_public: boolean;
    requires_document_processing: boolean;
    shares: string[];
    file_name: string;
    file_size: number;
    file_type: string;
  },
  file: File | RcFile,
): Promise<getFilehubPresignedURLResponse> => {
  const user = getUserData();
  const url = `/clients/${user.client_id}/filehub/create`;

  const formData = new FormData();

  formData.append('file', file);
  formData.append('owner_cognito_id', user.id);
  formData.append('is_public', params.is_public.toString());
  formData.append('requires_document_processing', params.requires_document_processing.toString());
  formData.append('owner_type', 'advisor');
  formData.append('shares', params.shares.join(','));
  formData.append('file_name', params.file_name);
  formData.append('file_size', params.file_size.toString());
  formData.append('file_type', params.file_type);

  try {
    const presignedUrlResponse = await service.post<getFilehubPresignedURLResponse>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return presignedUrlResponse;
  } catch (error) {
    console.error('Failed to upload profile media:', error);
    throw error;
  }
};

export type confirmFileResponse = {
  file: FilehubFilesData;
};

export const confirmUploadFile = async (
  file_id: string,
  file_name: string,
  owner_cognito_id: string,
  share_ids: string[],
): Promise<confirmFileResponse> => {
  const user = getUserData();
  const url = `/clients/${user.client_id}/filehub/file/${file_id}/confirm`;
  const data = {
    file_name: file_name,
    owner_cognito_id: owner_cognito_id,
    share_ids: share_ids,
  };
  try {
    const response = await service.post<confirmFileResponse>(url, data);
    return response;
  } catch (error) {
    console.error('Failed to confirm upload file:', error);
    throw error;
  }
};

export const getAdvisorFiles = async (): Promise<FilehubFilesResponse> => {
  const user = getUserData();
  const url = `/clients/${user.client_id}/filehub/owner/files`;
  try {
    const response = await service.getByParams<FilehubFilesResponse>(url);
    return response;
  } catch (error) {
    console.error('Failed to confirm upload file:', error);
    throw error;
  }
};

export const getReceivedAdvisorFiles = async (): Promise<FilehubFilesResponse> => {
  const user = getUserData();
  const url = `/clients/${user.client_id}/filehub/received/files`;
  try {
    const response = await service.getByParams<FilehubFilesResponse>(url);
    return response;
  } catch (error) {
    console.error('Failed to confirm upload file:', error);
    throw error;
  }
};

export const deleteAdvisorFile = async (file_id: string): Promise<DeleteFilehubFileResponse> => {
  const user = getUserData();
  const url = `/clients/${user.client_id}/filehub/advisor/files/${file_id}`;
  try {
    const response = await service.delByData<DeleteFilehubFileResponse>(url);
    return response;
  } catch (error) {
    console.error('Failed to delete advisor file:', error);
    throw error;
  }
};

export const getAdvisorFilesWithShares = async (params?: {
  pagination_key?: string;
  limit?: number;
  include_share_details?: boolean;
  search_keyword?: string;
  sort_by?: 'upload_at' | 'filename' | 'share_count';
  sort_order?: 'asc' | 'desc';
}): Promise<PaginationFilesResponse> => {
  const user = getUserData();
  const url = `/clients/${user.client_id}/filehub/owner/files-with-shares`;
  try {
    const response = await service.getByParams<PaginationFilesResponse>(url, params);
    return response;
  } catch (error) {
    console.error('Failed to get advisor files with shares:', error);
    throw error;
  }
};

export const getReceivedAdvisorFilesWithShares = async (params?: {
  pagination_key?: string;
  limit?: number;
  include_share_details?: boolean;
  search_keyword?: string;
  sort_by?: 'upload_at' | 'filename' | 'share_count';
  sort_order?: 'asc' | 'desc';
}): Promise<PaginationFilesResponse> => {
  const user = getUserData();
  const url = `/clients/${user.client_id}/filehub/received/files-with-shares`;
  try {
    const response = await service.getByParams<PaginationFilesResponse>(url, params);
    return response;
  } catch (error) {
    console.error('Failed to get received advisor files with shares:', error);
    throw error;
  }
};

export const getReceivedAdvisorFilesWithSharesByClientId = async (params?: {
  pagination_key?: string;
  limit?: number;
  include_share_details?: boolean;
  search_keyword?: string;
  sort_by?: 'upload_at' | 'filename' | 'share_count';
  sort_order?: 'asc' | 'desc';
}): Promise<PaginationFilesResponse> => {
  const user = getUserData();
  const url = `/clients/${user.client_id}/filehub/received/files-with-shares-by-client-id`;
  try {
    const response = await service.getByParams<PaginationFilesResponse>(url, params);
    return response;
  } catch (error) {
    console.error('Failed to get received advisor files with shares:', error);
    throw error;
  }
};

export type DownloadLinkResponse = {
  preview_url: string;
  download_url: string;
};

export const getSharedFileDownloadLink = async (file_id: string): Promise<DownloadLinkResponse> => {
  const user = getUserData();
  const url = `/clients/${user.client_id}/filehub/advisor/shared_files/${file_id}`;
  try {
    const response = await service.getByParams<DownloadLinkResponse>(url);
    return response;
  } catch (error) {
    console.error('Failed to get shared file download link:', error);
    throw error;
  }
};

export type UpdateTaxAddressRequest = {
  address: {
    city: string;
    country: string;
    line1: string;
    line2: string;
    postal_code: string;
    state: string;
  };
};

export const updateTaxAddress = async (body: UpdateTaxAddressRequest) => {
  const user = getUserData();
  const url = `/client/${user.client_id}/tax-address`;
  try {
    await service.put<DownloadLinkResponse>(url, body);
  } catch (error) {
    console.error('Failed to get shared file download link:', error);
    throw error;
  }
};

export type GetCorrespondentTransactionHistoryResponse = {
  current_balance: string; // decimal.Decimal → string
  collected_fees: Fee[]; // list[Fee]
  withdrawals: Withdrawal[]; // list[Withdrawal]
  fee_list_pagination_key?: string | null;
};

export type Withdrawal = {
  settlement_id: string;
  created_at: string; // datetime.datetime → string (ISO)
  updated_at: string; // datetime.datetime → string (ISO)
  amount: string; // decimal.Decimal → string
  state: string;
  is_platform: boolean;
};

export type Fee = {
  fee_id: string;
  account_id: string;
  fee_type: string;
  description: string;
  state: string;
  amount: string; // decimal.Decimal → string
  created_at: string; // datetime.datetime → string (ISO)
  updated_at: string; // datetime.datetime → string (ISO)
  platform_portion: string; // decimal.Decimal → string
};

export const getTransactionHistory = async (
  paginationKey: string | null,
  limit?: number,
): Promise<GetCorrespondentTransactionHistoryResponse> => {
  const user = getUserData();
  const url = `/clients/${user.client_id}/transaction-history`;
  try {
    return await service.getByParams<GetCorrespondentTransactionHistoryResponse>(url, {
      pagination_key: paginationKey,
      limit: limit ?? 64,
    });
  } catch (error) {
    console.error('Failed to get transaction history:', error);
    throw error;
  }
};

export type InvestorProfile = {
  id: string;
  firm_id: string;
  name: string | null;
  email?: string | null;
  phone_number?: string | null;
  date_of_birth?: string | null;
  advisor_cognito_id?: string | null;
  cognito_id?: string | null;
  identity_id?: string | null;
  progress?: string | null;
  lifecycle_status?: InvestorLifecycleStatus | string | null;
  created_at: string;
  updated_at: string;

  // Additional data
  aum?: number;
  goals?: string[];
};

export type CustomerProfile = InvestorProfile;

export type InvestorProfileWriteRequest = Partial<
  Pick<InvestorProfile, 'name' | 'email' | 'phone_number' | 'date_of_birth' | 'advisor_cognito_id'>
> & {
  lifecycle_status?: InvestorLifecycleStatus;
};

export type AddInvestorRequest = InvestorProfileWriteRequest;
export type AddCustomerRequest = AddInvestorRequest;

export type InvestorProfileUpdate = InvestorProfileWriteRequest;
export type CustomerProfileUpdate = InvestorProfileUpdate;

type InvestorResponse = {
  status: string;
  message: string;
  investor: InvestorProfile;
};

type InvestorsResponse = {
  investors: InvestorProfile[];
};

export type InvestorProfilePayload = Record<string, unknown>;

type InvestorProfilePayloadResponse = {
  status: string;
  message: string;
  data: InvestorProfilePayload;
};

export type InvestorProfilePayloadPatch = {
  expected_revision: string;
  idempotency_key: string;
  update: Record<string, unknown>;
};

export type InvestorProfilePayloadUpdateResult = {
  profile: InvestorProfilePayload;
  updated_paths: string[];
};

type InvestorProfilePayloadUpdateResponse = {
  status: string;
  message: string;
  data: InvestorProfilePayloadUpdateResult;
};

export const INVESTOR_PROFILE_CONFLICT_MESSAGE =
  'Profile data changed. Refresh the profile and try again.';
export const INVESTOR_PROFILE_VALIDATION_FAILED_MESSAGE =
  'Profile update is invalid. Check the highlighted fields and try again.';
export const ADVISOR_PROFILE_MEMORY_CONFLICT_MESSAGE =
  'Profile Memory changed. Refresh and try again.';
export const ADVISOR_PROFILE_MEMORY_VALIDATION_FAILED_MESSAGE =
  'Profile memory update is invalid. Check the value and try again.';

export type AdvisorProfileMemorySource = {
  id?: string;
  source_type: string;
  source_ref?: string | null;
  provenance?: Record<string, unknown> | null;
  observed_at?: string | null;
  recorded_at?: string | null;
};

export type AdvisorProfileMemoryReadState = 'active' | 'removed';

export type AdvisorProfileMemoryCounts = {
  active: number;
  removed: number;
  needs_review: number;
};

export type AdvisorProfileMemoryCuration = {
  state?: 'ACTIVE' | 'REMOVED' | 'PROMOTED' | string;
  version?: number;
  label?: {
    type?: string;
    observation_id?: string | null;
    removed_by_ref?: string | null;
    by_display_name?: string | null;
    removed_at?: string | null;
    removal_reason?: string | null;
    new_value?: string | null;
    new_source_type?: string | null;
    observed_at?: string | null;
  } | null;
  removed?: {
    by_ref?: string | null;
    by_display_name?: string | null;
    at?: string | null;
    reason?: string | null;
  } | null;
  edited?: {
    by_ref?: string | null;
    by_display_name?: string | null;
    at?: string | null;
    original_value?: string | null;
  } | null;
};

export type AdvisorProfileMemoryProfileLink = {
  status: 'missing' | 'differs' | 'matches' | 'unmapped' | string;
  kind?: 'scalar' | 'list' | string | null;
  target_path?: string | null;
  section_id?: string | null;
  section_label?: string | null;
  field_label?: string | null;
  current_display_value?: string | null;
  suggested_value?: unknown;
  suggested_row?: Record<string, unknown> | null;
  client_visible?: boolean;
  eligible_targets?: Array<Record<string, string>>;
};

export type AdvisorProfileMemoryItem = {
  id: string;
  memory_key?: string;
  name: string;
  value: string;
  source_type: string;
  source_ref?: string | null;
  provenance?: Record<string, unknown> | null;
  sources?: AdvisorProfileMemorySource[];
  advisor_first_name?: string | null;
  advisor_last_name?: string | null;
  advisor_full_name?: string | null;
  advisor_recorded_at?: string | null;
  meeting_title?: string | null;
  meeting_started_at?: string | null;
  meeting_summary_file_key?: string | null;
  meeting_transcript_file_key?: string | null;
  meeting_job_id?: string | null;
  advisor_profile_visible: boolean;
  investor_portal_visible: boolean;
  observed_at?: string | null;
  recorded_at?: string | null;
  curation?: AdvisorProfileMemoryCuration;
  profile_link?: AdvisorProfileMemoryProfileLink | null;
};

export type AdvisorProfileMemoryRemovalReason =
  | 'INCORRECT'
  | 'OUTDATED'
  | 'DUPLICATE'
  | 'NOT_ABOUT_CLIENT';

export type AdvisorProfileMemoryCurationRequest = {
  expected_version: number;
  idempotency_key: string;
};

export type AdvisorProfileMemoryEditRequest = AdvisorProfileMemoryCurationRequest & {
  value: string;
  name?: string;
};

export type AdvisorMemoryDraft = { name: string; value: string };
export type AdvisorMemoryAddRequest = AdvisorMemoryDraft & AdvisorProfileMemoryCurationRequest;
export type AdvisorMemoryDraftPreview = {
  memory_key: string;
  can_save: boolean;
  error_message: string | null;
  destination_path: string | null;
  destination_label: string | null;
  existing: AdvisorProfileMemoryItem | null;
  profile_link: AdvisorProfileMemoryProfileLink;
};

export type AdvisorProfileMemoryRemoveRequest = AdvisorProfileMemoryCurationRequest & {
  reason?: AdvisorProfileMemoryRemovalReason | null;
};

export type AdvisorProfileMemoryPromoteRequest = AdvisorProfileMemoryCurationRequest & {
  profile_update: Record<string, unknown>;
};

type AdvisorProfileMemoryMutationResponse = {
  status: string;
  message: string;
  enabled?: boolean;
  reason_code?: string;
  item?: AdvisorProfileMemoryItem | null;
  profile?: InvestorProfilePayload | null;
  replayed?: boolean;
};

export type AdvisorProfileMemoryMutationResult = {
  enabled: boolean;
  reasonCode: string;
  item: AdvisorProfileMemoryItem | null;
  profile: InvestorProfilePayload | null;
  replayed: boolean;
};

export type AdvisorProfileMemoryHistoryEntry = Record<string, unknown> & {
  occurred_at?: string | null;
  source_type?: string | null;
  version?: number;
  event_type?: string | null;
  operation?: string | null;
  action?: string | null;
  actor_ref?: string | null;
  by_display_name?: string | null;
  at?: string | null;
  created_at?: string | null;
  recorded_at?: string | null;
  value?: string | null;
  previous_value?: string | null;
  reason?: string | null;
};

type AdvisorProfileMemoryHistoryResponse = {
  status: string;
  message: string;
  enabled?: boolean;
  reason_code?: string;
  data: AdvisorProfileMemoryHistoryEntry[];
};

export type AdvisorProfileMemoryHistoryResult = {
  enabled: boolean;
  reasonCode: string;
  entries: AdvisorProfileMemoryHistoryEntry[];
};

type AdvisorProfileMemoryResponse = {
  status: string;
  message: string;
  enabled?: boolean;
  reason_code?: string;
  curation_enabled?: boolean;
  counts?: Partial<AdvisorProfileMemoryCounts>;
  data: AdvisorProfileMemoryItem[];
};

export type AdvisorProfileMemoryResult = {
  enabled: boolean;
  reasonCode: string;
  curationEnabled: boolean;
  counts: AdvisorProfileMemoryCounts;
  items: AdvisorProfileMemoryItem[];
};

export type GetAdvisorProfileMemoryOptions = {
  state?: AdvisorProfileMemoryReadState;
};

const EMPTY_PROFILE_MEMORY_COUNTS: AdvisorProfileMemoryCounts = {
  active: 0,
  removed: 0,
  needs_review: 0,
};

const profileMemoryCountsFromResponse = (
  counts: Partial<AdvisorProfileMemoryCounts> | undefined,
  fallbackItemCount: number,
): AdvisorProfileMemoryCounts => ({
  active: typeof counts?.active === 'number' ? counts.active : fallbackItemCount,
  removed: typeof counts?.removed === 'number' ? counts.removed : 0,
  needs_review: typeof counts?.needs_review === 'number' ? counts.needs_review : 0,
});

const isInvestorProfilePayloadResponse = (
  response: InvestorProfilePayload | InvestorProfilePayloadResponse,
): response is InvestorProfilePayloadResponse =>
  typeof response.status === 'string' &&
  typeof response.message === 'string' &&
  response.data !== null &&
  typeof response.data === 'object';

const isInvestorProfilePayloadUpdateResponse = (
  response: InvestorProfilePayloadUpdateResult | InvestorProfilePayloadUpdateResponse,
): response is InvestorProfilePayloadUpdateResponse =>
  typeof response.status === 'string' &&
  typeof response.message === 'string' &&
  response.data !== null &&
  typeof response.data === 'object' &&
  'profile' in response.data;

const isAdvisorProfileMemoryResponse = (
  response: unknown,
): response is AdvisorProfileMemoryResponse => {
  if (response === null || typeof response !== 'object') return false;
  const candidate = response as Partial<AdvisorProfileMemoryResponse>;
  return (
    typeof candidate.status === 'string' &&
    typeof candidate.message === 'string' &&
    Array.isArray(candidate.data)
  );
};

const advisorProfileMemoryResultFromResponse = (response: unknown): AdvisorProfileMemoryResult => {
  if (isAdvisorProfileMemoryResponse(response)) {
    const enabled = typeof response.enabled === 'boolean' ? response.enabled : true;
    const curationEnabled =
      typeof response.curation_enabled === 'boolean' ? response.curation_enabled : false;
    return {
      enabled,
      reasonCode:
        typeof response.reason_code === 'string'
          ? response.reason_code
          : enabled
            ? 'enabled'
            : 'feature_flag_disabled',
      curationEnabled,
      counts: profileMemoryCountsFromResponse(response.counts, response.data.length),
      items: response.data,
    };
  }
  throw new Error('Invalid Profile Memory response');
};

const advisorProfileMemoryMutationResultFromResponse = (
  response: AdvisorProfileMemoryMutationResponse,
): AdvisorProfileMemoryMutationResult => {
  if (response.enabled === false) {
    throw new Error('Profile Memory is not available. Refresh and try again.');
  }
  return {
    enabled: typeof response.enabled === 'boolean' ? response.enabled : true,
    reasonCode: typeof response.reason_code === 'string' ? response.reason_code : 'enabled',
    item: response.item ?? null,
    profile: response.profile ?? null,
    replayed: typeof response.replayed === 'boolean' ? response.replayed : false,
  };
};

const advisorProfileMemoryHistoryResultFromResponse = (
  response: AdvisorProfileMemoryHistoryResponse,
): AdvisorProfileMemoryHistoryResult => ({
  enabled: typeof response.enabled === 'boolean' ? response.enabled : true,
  reasonCode: typeof response.reason_code === 'string' ? response.reason_code : 'enabled',
  entries: Array.isArray(response.data) ? response.data : [],
});

const advisorProfileMemoryPath = (firmId: string, investorId: string, memoryKey: string): string =>
  `/firms/${firmId}/investors/${investorId}/profile-memory/${encodeURIComponent(memoryKey)}`;

const profileUpdateConflictMessage = (error: unknown): string | null => {
  if (error === null || typeof error !== 'object' || !('response' in error)) {
    return null;
  }
  const response = (error as { response?: { status?: unknown; data?: unknown } }).response;
  if (response?.status !== 409) {
    return null;
  }
  const data = response.data;
  if (data === null || typeof data !== 'object' || !('detail' in data)) {
    return null;
  }
  const detail = (data as { detail?: unknown }).detail;
  return detail === INVESTOR_PROFILE_CONFLICT_MESSAGE ? INVESTOR_PROFILE_CONFLICT_MESSAGE : null;
};

const profileUpdateValidationMessage = (error: unknown): string | null => {
  if (error === null || typeof error !== 'object' || !('response' in error)) {
    return null;
  }
  const response = (error as { response?: { status?: unknown; data?: unknown } }).response;
  if (response?.status !== 400) {
    return null;
  }
  const data = response.data;
  if (data === null || typeof data !== 'object' || !('detail' in data)) {
    return null;
  }
  const detail = (data as { detail?: unknown }).detail;
  if (detail === null || typeof detail !== 'object') {
    return null;
  }
  if ((detail as { type?: unknown }).type !== 'PROFILE_VALIDATION_FAILED') {
    return null;
  }
  const message = (detail as { message?: unknown }).message;
  return typeof message === 'string' && message.trim()
    ? message
    : INVESTOR_PROFILE_VALIDATION_FAILED_MESSAGE;
};

const profileUpdateClientErrorMessage = (error: unknown): string | null =>
  profileUpdateConflictMessage(error) ?? profileUpdateValidationMessage(error);

const responseDetailFromError = (error: unknown): unknown => {
  if (error === null || typeof error !== 'object' || !('response' in error)) {
    return null;
  }

  const response = (error as { response?: { data?: unknown } }).response;
  const data = response?.data;
  if (data === null || typeof data !== 'object' || !('detail' in data)) {
    return null;
  }

  return (data as { detail?: unknown }).detail;
};

const responseDetailMessage = (detail: unknown): string | null => {
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail !== null && typeof detail === 'object') {
    const message = (detail as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  if (Array.isArray(detail)) {
    for (const issue of detail) {
      if (issue !== null && typeof issue === 'object') {
        const message = (issue as { msg?: unknown; message?: unknown }).msg;
        if (typeof message === 'string' && message.trim()) return message;
        const fallbackMessage = (issue as { message?: unknown }).message;
        if (typeof fallbackMessage === 'string' && fallbackMessage.trim()) return fallbackMessage;
      }
    }
  }
  return null;
};

const profileMemoryMutationClientErrorMessage = (error: unknown): string | null => {
  if (error === null || typeof error !== 'object' || !('response' in error)) {
    return null;
  }
  const response = (error as { response?: { status?: unknown } }).response;
  const detail = responseDetailFromError(error);
  if (response?.status === 409) {
    return responseDetailMessage(detail) ?? ADVISOR_PROFILE_MEMORY_CONFLICT_MESSAGE;
  }
  if (response?.status === 400) {
    return responseDetailMessage(detail) ?? ADVISOR_PROFILE_MEMORY_VALIDATION_FAILED_MESSAGE;
  }
  if (response?.status === 404) {
    return 'Profile Memory item not found. Refresh and try again.';
  }
  return null;
};

export type CreateInvestorOptions = {
  idempotencyKey?: string;
  restoreArchived?: boolean;
};

type CreateInvestorRequest = AddInvestorRequest & {
  restore_archived?: boolean;
};

const investorEditablePayloadFrom = (
  data: AddInvestorRequest | InvestorProfileUpdate,
): InvestorProfileUpdate => {
  const payload: InvestorProfileUpdate = {};

  if ('name' in data) payload.name = data.name;
  if ('email' in data) payload.email = data.email;
  if ('phone_number' in data) payload.phone_number = data.phone_number;
  if ('date_of_birth' in data) payload.date_of_birth = data.date_of_birth;
  if ('advisor_cognito_id' in data) payload.advisor_cognito_id = data.advisor_cognito_id;
  if ('lifecycle_status' in data) payload.lifecycle_status = data.lifecycle_status;

  return payload;
};

export const createInvestor = async (
  investorData: AddInvestorRequest,
  options: CreateInvestorOptions = {},
): Promise<InvestorProfile> => {
  const user = getUserData();
  const requestData: CreateInvestorRequest = {
    ...investorEditablePayloadFrom(investorData),
    advisor_cognito_id: investorData.advisor_cognito_id ?? user.id,
  };
  if (options.restoreArchived) {
    requestData.restore_archived = true;
  }
  try {
    const response = await service.post<InvestorProfile>(
      `/firms/${user.client_id}/investors`,
      requestData,
      options.idempotencyKey
        ? {
            headers: {
              'Idempotency-Key': options.idempotencyKey,
            },
          }
        : undefined,
    );
    return response;
  } catch (error) {
    console.error('Error creating investor:', error);
    throw error;
  }
};

export const createCustomer = createInvestor;

export const getInvestors = async (
  options: {
    limit?: number;
    offset?: number;
    no_advisor?: boolean;
    advisor?: string;
  } = {},
): Promise<InvestorProfile[]> => {
  try {
    const user = getUserData();
    // Build query parameters
    const params = new URLSearchParams();
    if (options.limit !== undefined) params.append('limit', options.limit.toString());
    if (options.offset !== undefined) params.append('offset', options.offset.toString());
    if (options.no_advisor) params.append('no_advisor', 'true');
    if (options.advisor) params.append('advisor', options.advisor);

    const queryString = params.toString();
    const url = `/firms/${user.client_id}/investors${queryString ? `?${queryString}` : ''}`;

    const response = await service.getByParams<InvestorsResponse>(
      url,
      {},
      {
        headers: {
          'wavvest-version': INVESTOR_API_VERSION,
        },
      },
    );

    return response.investors;
  } catch (error) {
    console.error('Error getting investors:', error);
    throw error;
  }
};

export const getCustomers = getInvestors;

export const getInvestorProfilePayload = async (
  investorId: string,
): Promise<InvestorProfilePayload> => {
  const user = getUserData();
  try {
    const response = await service.getByParams<
      InvestorProfilePayload | InvestorProfilePayloadResponse
    >(
      `/firms/${user.client_id}/investors/${investorId}/profile`,
      {},
      {
        preserveResponseDataEnvelope: true,
        headers: {
          'wavvest-version': INVESTOR_API_VERSION,
        },
      },
    );

    return isInvestorProfilePayloadResponse(response) ? response.data : response;
  } catch (error) {
    console.error('Error getting investor profile payload:', error);
    throw error;
  }
};

export const getAdvisorProfileMemory = async (
  investorId: string,
  options: GetAdvisorProfileMemoryOptions = {},
): Promise<AdvisorProfileMemoryResult> => {
  const user = getUserData();
  try {
    const response = await service.getByParams<unknown>(
      `/firms/${user.client_id}/investors/${investorId}/profile-memory`,
      options.state ? { state: options.state } : {},
      {
        preserveResponseDataEnvelope: true,
        headers: {
          'wavvest-version': INVESTOR_API_VERSION,
        },
      },
    );

    return advisorProfileMemoryResultFromResponse(response);
  } catch (error) {
    console.error('Error getting advisor profile memory:', error);
    throw error;
  }
};

export const previewAdvisorMemory = async (
  investorId: string,
  draft: AdvisorMemoryDraft,
): Promise<AdvisorMemoryDraftPreview> => {
  const user = getUserData();
  const response = await service.post<{
    enabled: boolean;
    preview: AdvisorMemoryDraftPreview | null;
  }>(`${advisorProfileMemoryPath(user.client_id, investorId, 'advisor-notes')}/preview`, draft, {
    preserveResponseDataEnvelope: true,
    headers: { 'wavvest-version': INVESTOR_API_VERSION },
  });
  if (!response.enabled || !response.preview)
    throw new Error('Adding profile memory is currently unavailable.');
  return response.preview;
};

export const addAdvisorMemory = async (
  investorId: string,
  draft: AdvisorMemoryAddRequest,
): Promise<AdvisorProfileMemoryMutationResult> => {
  const user = getUserData();
  try {
    const response = await service.post<AdvisorProfileMemoryMutationResponse>(
      advisorProfileMemoryPath(user.client_id, investorId, 'advisor-notes'),
      draft,
      { preserveResponseDataEnvelope: true, headers: { 'wavvest-version': INVESTOR_API_VERSION } },
    );
    return advisorProfileMemoryMutationResultFromResponse(response);
  } catch (error) {
    const clientErrorMessage = profileMemoryMutationClientErrorMessage(error);
    if (clientErrorMessage)
      throw Object.assign(new Error(clientErrorMessage), {
        status: (error as { response?: { status?: number } }).response?.status,
      });
    throw error;
  }
};

export const editAdvisorProfileMemory = async (
  investorId: string,
  memoryKey: string,
  payload: AdvisorProfileMemoryEditRequest,
): Promise<AdvisorProfileMemoryMutationResult> => {
  const user = getUserData();
  try {
    const response = await service.patch<AdvisorProfileMemoryMutationResponse>(
      advisorProfileMemoryPath(user.client_id, investorId, memoryKey),
      payload,
      {
        preserveResponseDataEnvelope: true,
        headers: {
          'wavvest-version': INVESTOR_API_VERSION,
        },
      },
    );

    return advisorProfileMemoryMutationResultFromResponse(response);
  } catch (error) {
    console.error('Error editing advisor profile memory:', error);
    const clientErrorMessage = profileMemoryMutationClientErrorMessage(error);
    if (clientErrorMessage) {
      throw Object.assign(new Error(clientErrorMessage), {
        status: (error as { response?: { status?: number } }).response?.status,
      });
    }
    throw error;
  }
};

export const removeAdvisorProfileMemory = async (
  investorId: string,
  memoryKey: string,
  payload: AdvisorProfileMemoryRemoveRequest,
): Promise<AdvisorProfileMemoryMutationResult> => {
  const user = getUserData();
  try {
    const requestPayload =
      payload.reason === null || payload.reason === undefined
        ? {
            expected_version: payload.expected_version,
            idempotency_key: payload.idempotency_key,
          }
        : payload;
    const response = await service.post<AdvisorProfileMemoryMutationResponse>(
      `${advisorProfileMemoryPath(user.client_id, investorId, memoryKey)}/remove`,
      requestPayload,
      {
        preserveResponseDataEnvelope: true,
        headers: {
          'wavvest-version': INVESTOR_API_VERSION,
        },
      },
    );

    return advisorProfileMemoryMutationResultFromResponse(response);
  } catch (error) {
    console.error('Error removing advisor profile memory:', error);
    const clientErrorMessage = profileMemoryMutationClientErrorMessage(error);
    if (clientErrorMessage) {
      throw new Error(clientErrorMessage);
    }
    throw error;
  }
};

export const restoreAdvisorProfileMemory = async (
  investorId: string,
  memoryKey: string,
  payload: AdvisorProfileMemoryCurationRequest,
): Promise<AdvisorProfileMemoryMutationResult> => {
  const user = getUserData();
  try {
    const response = await service.post<AdvisorProfileMemoryMutationResponse>(
      `${advisorProfileMemoryPath(user.client_id, investorId, memoryKey)}/restore`,
      payload,
      {
        preserveResponseDataEnvelope: true,
        headers: {
          'wavvest-version': INVESTOR_API_VERSION,
        },
      },
    );

    return advisorProfileMemoryMutationResultFromResponse(response);
  } catch (error) {
    console.error('Error restoring advisor profile memory:', error);
    const clientErrorMessage = profileMemoryMutationClientErrorMessage(error);
    if (clientErrorMessage) {
      throw new Error(clientErrorMessage);
    }
    throw error;
  }
};

export const resolveAdvisorProfileMemoryLabel = async (
  investorId: string,
  memoryKey: string,
  payload: AdvisorProfileMemoryCurationRequest,
): Promise<AdvisorProfileMemoryMutationResult> => {
  const user = getUserData();
  try {
    const response = await service.post<AdvisorProfileMemoryMutationResponse>(
      `${advisorProfileMemoryPath(user.client_id, investorId, memoryKey)}/resolve-label`,
      payload,
      {
        preserveResponseDataEnvelope: true,
        headers: {
          'wavvest-version': INVESTOR_API_VERSION,
        },
      },
    );

    return advisorProfileMemoryMutationResultFromResponse(response);
  } catch (error) {
    console.error('Error resolving advisor profile memory label:', error);
    const clientErrorMessage = profileMemoryMutationClientErrorMessage(error);
    if (clientErrorMessage) {
      throw new Error(clientErrorMessage);
    }
    throw error;
  }
};

export const promoteAdvisorProfileMemory = async (
  investorId: string,
  memoryKey: string,
  payload: AdvisorProfileMemoryPromoteRequest,
): Promise<AdvisorProfileMemoryMutationResult> => {
  const user = getUserData();
  try {
    const response = await service.post<AdvisorProfileMemoryMutationResponse>(
      `${advisorProfileMemoryPath(user.client_id, investorId, memoryKey)}/promote`,
      payload,
      {
        preserveResponseDataEnvelope: true,
        headers: {
          'wavvest-version': INVESTOR_API_VERSION,
        },
      },
    );

    return advisorProfileMemoryMutationResultFromResponse(response);
  } catch (error) {
    console.error('Error promoting advisor profile memory:', error);
    const clientErrorMessage = profileMemoryMutationClientErrorMessage(error);
    if (clientErrorMessage) {
      throw new Error(clientErrorMessage);
    }
    throw error;
  }
};

export const undoAdvisorProfileMemoryPromotion = async (
  investorId: string,
  memoryKey: string,
  payload: AdvisorProfileMemoryCurationRequest,
): Promise<AdvisorProfileMemoryMutationResult> => {
  const user = getUserData();
  try {
    const response = await service.post<AdvisorProfileMemoryMutationResponse>(
      `${advisorProfileMemoryPath(user.client_id, investorId, memoryKey)}/promote/undo`,
      payload,
      {
        preserveResponseDataEnvelope: true,
        headers: {
          'wavvest-version': INVESTOR_API_VERSION,
        },
      },
    );

    return advisorProfileMemoryMutationResultFromResponse(response);
  } catch (error) {
    console.error('Error undoing advisor profile memory promotion:', error);
    const clientErrorMessage = profileMemoryMutationClientErrorMessage(error);
    if (clientErrorMessage) {
      throw new Error(clientErrorMessage);
    }
    throw error;
  }
};

export const getAdvisorProfileMemoryHistory = async (
  investorId: string,
  memoryKey: string,
): Promise<AdvisorProfileMemoryHistoryResult> => {
  const user = getUserData();
  try {
    const response = await service.getByParams<AdvisorProfileMemoryHistoryResponse>(
      `${advisorProfileMemoryPath(user.client_id, investorId, memoryKey)}/history`,
      {},
      {
        preserveResponseDataEnvelope: true,
        headers: {
          'wavvest-version': INVESTOR_API_VERSION,
        },
      },
    );

    return advisorProfileMemoryHistoryResultFromResponse(response);
  } catch (error) {
    console.error('Error getting advisor profile memory history:', error);
    throw error;
  }
};

export const updateInvestorProfilePayload = async (
  investorId: string,
  payload: InvestorProfilePayloadPatch,
): Promise<InvestorProfilePayloadUpdateResult> => {
  const user = getUserData();
  try {
    const response = await service.patch<
      InvestorProfilePayloadUpdateResult | InvestorProfilePayloadUpdateResponse
    >(`/firms/${user.client_id}/investors/${investorId}/profile`, payload, {
      preserveResponseDataEnvelope: true,
      headers: {
        'wavvest-version': INVESTOR_API_VERSION,
      },
    });

    return isInvestorProfilePayloadUpdateResponse(response) ? response.data : response;
  } catch (error) {
    console.error('Error updating investor profile payload:', error);
    const clientErrorMessage = profileUpdateClientErrorMessage(error);
    if (clientErrorMessage) {
      throw new Error(clientErrorMessage);
    }
    throw error;
  }
};

export const updateInvestor = async (
  investorId: string,
  updateData: InvestorProfileUpdate,
): Promise<InvestorProfile> => {
  const user = getUserData();
  const requestData = investorEditablePayloadFrom(updateData);
  try {
    const response = await service.post<InvestorResponse>(
      `/firms/${user.client_id}/investors/${investorId}/update`,
      requestData,
      {
        headers: {
          'wavvest-version': INVESTOR_API_VERSION,
        },
      },
    );

    return response.investor;
  } catch (error) {
    console.error('Error updating investor:', error);
    throw error;
  }
};

export const updateCustomer = updateInvestor;

// todo Han
export const removeInvestor = async (
  investorId: string,
  reason: string,
): Promise<InvestorProfile> => {
  const user = getUserData();
  try {
    const response = await service.delByData<InvestorResponse>(
      `/firms/${user.client_id}/investors/${investorId}`,
      { reason: reason.trim() },
    );

    return response.investor;
  } catch (error) {
    console.error('Error removing investor:', error);
    throw error;
  }
};

export const removeCustomer = removeInvestor;

export type InvestorAgreement = {
  client_id: string;
  agreement_id: string;
  url: string;
  title: string;
  description: string;
  enabled: boolean;
  created_at: string;
  last_updated_at: string;
  version: string;
};

export type InvestorSignedAgreementSignature = {
  customer_cognito_id: string;
  agreement_id: string;
  version: string;
  signed_at: string;
  consent_type: string;
};

export type InvestorSignedAgreement = {
  signature: InvestorSignedAgreementSignature;
  agreement: InvestorAgreement;
};

export type GetInvestorSignedAgreementsResponse = InvestorSignedAgreement[];

export type CustomerAgreement = InvestorAgreement;
export type CustomerSignedAgreementSignature = InvestorSignedAgreementSignature;
export type CustomerSignedAgreement = InvestorSignedAgreement;
export type GetCustomerSignedAgreementsResponse = GetInvestorSignedAgreementsResponse;

export const getInvestorSignedAgreements = async (
  investorId: string,
): Promise<GetInvestorSignedAgreementsResponse> => {
  const url = `/customers/${investorId}/signed-agreements`;
  try {
    const response = await service.getByParams<MaybeDataEnvelope<InvestorSignedAgreement>>(url);
    return unwrapDataArray<InvestorSignedAgreement>(response);
  } catch (error) {
    console.error('Failed to fetch investor signed agreements:', error);
    throw error;
  }
};

export const getCustomerSignedAgreements = getInvestorSignedAgreements;

export type RiskScoreRecord = {
  id: string;
  user_id: string;
  firm_id: string;
  account_id: string | null;
  risk_score: number;
};

export type GetRiskScoresQuery = {
  firm_id: string;
  user_id: string;
};

export const getRiskScores = async (query: GetRiskScoresQuery): Promise<RiskScoreRecord[]> => {
  const firmId = query.firm_id?.trim();
  const userId = query.user_id?.trim();
  if (!firmId) {
    throw new Error('firm_id is required and cannot be empty');
  }
  if (!userId) {
    throw new Error('user_id is required and cannot be empty');
  }

  try {
    // `/api/risk-scores` is IAM-locked for client-portal-backend. Advisor JWT
    // calls must use this proxy path so API Gateway CORS/auth match other /api routes.
    const response = await service.getByParams<MaybeDataEnvelope<RiskScoreRecord>>(
      '/investors/risk-scores',
      {
        firm_id: firmId,
        user_id: userId,
      },
    );
    return unwrapDataArray<RiskScoreRecord>(response);
  } catch (error) {
    console.error('Failed to fetch risk scores:', error);
    throw error;
  }
};
